/**
 * Header component with currency, inventory, and rate displays
 */

import { useState } from 'react';
import { useCurrency, useInventoryCount, useInventoryCapacity } from '@/state';
import { formatCurrency, formatRate } from '@/utils';
import { useStatRates } from '@/hooks/useStatRates';
import { DEBUG_ENABLED, DEBUG_SHORTCUT } from '@/config/debug';
import { soundManager } from '@/services/SoundManager';
import { AnimatedCounter } from '@/components/common/AnimatedCounter';

interface HeaderProps {
  onDebugToggle?: () => void;
}

export function Header({ onDebugToggle }: HeaderProps) {
  const currency = useCurrency();
  const inventoryCount = useInventoryCount();
  const inventoryCapacity = useInventoryCapacity();
  const { global } = useStatRates(1000);
  const [muted, setMuted] = useState(soundManager.isMuted());

  const handleMuteToggle = () => {
    soundManager.toggleMute();
    setMuted(soundManager.isMuted());
  };

  return (
    <header className="bg-surface-primary border-b border-border-default px-4 py-3 shrink-0">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-blue-400 hidden sm:block">🎣 Fishing Empire</h1>
          <h1 className="text-2xl font-bold text-blue-400 sm:hidden">🎣</h1>

          {/* Debug Button (only if DEBUG_ENABLED) */}
          {DEBUG_ENABLED && onDebugToggle && (
            <button
              onClick={onDebugToggle}
              className="w-8 h-8 bg-red-600 hover:bg-red-700 rounded-lg flex items-center justify-center text-sm transition-all hover:scale-110 border border-red-500"
              title={`Toggle Debug Menu (${DEBUG_SHORTCUT.ctrl ? 'Ctrl+' : ''}${DEBUG_SHORTCUT.shift ? 'Shift+' : ''}${DEBUG_SHORTCUT.key})`}
            >
              🔧
            </button>
          )}
        </div>

        <div className="flex gap-4 sm:gap-6 items-center">
          <div className="flex items-center gap-2">
            <span className="text-yellow-400 text-xl">💰</span>
            <AnimatedCounter
              value={currency}
              format={formatCurrency}
              className="text-lg font-semibold"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-blue-400 text-xl">🐟</span>
            <span className="text-lg">
              {inventoryCount} / {inventoryCapacity}
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-3 text-sm text-gray-400 border-l border-gray-600 pl-4">
            <span data-testid="fish-per-hour">{formatRate(global.fishPerHour)} fish/hr</span>
            <span data-testid="gold-per-hour">{formatRate(global.currencyPerHour)} $/hr</span>
          </div>

          {/* Mute Toggle */}
          <button
            onClick={handleMuteToggle}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-lg transition-all hover:scale-110 hover:bg-white/10"
            title={muted ? 'Unmute sounds' : 'Mute sounds'}
            aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
          >
            {muted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>
    </header>
  );
}

// formatRate re-exported for backward compat (canonical source: @/utils/formatting)
export { formatRate } from '@/utils';
